import React, { useEffect, useState } from 'react';
import './App.css';
import AuthCallback from './components/AuthCallback';
import ChatWindow from './components/ChatWindow';
import SignIn from './components/SignIn';
import apiService from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthCallback, setIsAuthCallback] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hello! I\'m Wolly, your intelligent assistant. How can I help you today?',
      sender: 'bot',
      timestamp: new Date()
    }
  ]);

  // Check if this is an OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('access_token') || urlParams.has('error')) {
      setIsAuthCallback(true);
      setIsLoading(false);
    } else {
      checkExistingAuth();
    }
  }, []);

  const checkExistingAuth = async () => {
    const savedUser = localStorage.getItem('currentUser');
    const token = apiService.getToken();

    if (savedUser && token) {
      try {
        // Verify token is still valid
        const verification = await apiService.verifyToken(token);
        if (verification.valid) {
          setUser(JSON.parse(savedUser));
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('currentUser');
          apiService.removeToken();
        }
      } catch (e) {
        localStorage.removeItem('currentUser');
        apiService.removeToken();
      }
    }
    setIsLoading(false);
  };

  const handleSignIn = (userData) => {
    setUser(userData);
    // Welcome message for the user
    setMessages([
      {
        id: 1,
        text: `Hello, ${userData.name}! 👋 I'm Wolly, your intelligent assistant powered by Claude. How can I help you today?`,
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const handleAuthCallback = (userData) => {
    setIsAuthCallback(false);
    handleSignIn(userData);
  };

  const handleAuthError = (error) => {
    console.error('Auth error:', error);
    setIsAuthCallback(false);
    apiService.removeToken();
    localStorage.removeItem('currentUser');
  };

  const handleSignOut = () => {
    localStorage.removeItem('currentUser');
    apiService.removeToken();
    setUser(null);
    setMessages([
      {
        id: 1,
        text: 'Hello! I\'m Wolly, your intelligent assistant. How can I help you today?',
        sender: 'bot',
        timestamp: new Date()
      }
    ]);
  };

  const handleSendMessage = async (text) => {
    const newMessage = {
      id: messages.length + 1,
      text,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);

    // Add a temporary "typing" indicator
    const typingId = messages.length + 2;
    const typingMessage = {
      id: typingId,
      text: '...',
      sender: 'bot',
      timestamp: new Date(),
      isTyping: true
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      // Call real API
      const response = await apiService.sendChatMessage(text);
      
      // Remove typing indicator and add real response
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== typingId);
        return [
          ...filtered,
          {
            id: typingId,
            text: response.assistant,
            sender: 'bot',
            timestamp: new Date(),
            isTyping: false
          }
        ];
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Remove typing indicator and show error
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== typingId);
        return [
          ...filtered,
          {
            id: typingId,
            text: 'Sorry, I encountered an error processing your message. Please try again.',
            sender: 'bot',
            timestamp: new Date(),
            isError: true
          }
        ];
      });
    }
  };

  if (isLoading) {
    return (
      <div className="App loading">
        <div className="spinner-large"></div>
      </div>
    );
  }

  // Handle OAuth callback
  if (isAuthCallback) {
    return (
      <AuthCallback 
        onSuccess={handleAuthCallback}
        onError={handleAuthError}
      />
    );
  }

  // If user is not authenticated, show sign in form
  if (!user) {
    return <SignIn onSignIn={handleSignIn} />;
  }

  return (
    <div className="App">
      <ChatWindow 
        messages={messages}
        onSendMessage={handleSendMessage}
        botName="Wolly"
        botAvatar="🤖"
        user={user}
        onSignOut={handleSignOut}
      />
    </div>
  );
}

export default App;

